import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type RoadmapTriageEntry, useCloseRoadmapTriage } from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";

import { Field, TEXTAREA_CLASS } from "./item-form";
import { StepButtons } from "./step-buttons";
import { triageKind } from "./triage";
import { TriageEntryTitle, useTriageReturnFocus } from "./triage-dialog-parts";

/** The longest note the server takes when an entry is closed. */
export const CLOSE_NOTE_MAX = 500;

interface CloseTriageDialogProps {
  /** The entry to close; it stays while the dialog closes. */
  entry: RoadmapTriageEntry | null;
  /**
   * The entry has left the triage since the page loaded (`triageEntryGone`):
   * another developer, or Claude, handled it. The form says so and closes nothing.
   */
  entryGone?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Sluiten on a triage entry, asked first: the entry's title, an optional
 * note on why, and Annuleren or Sluiten. A closed entry leaves the triage
 * without going on the roadmap. Should the entry leave the triage while the
 * dialog is open (handled elsewhere, most likely from Claude), the form says
 * so and Sluiten stays off. The dialog has no trigger of its own: it
 * gives the focus back to the row's action, or to the row's menu, or, the
 * entry having left, to the group's toggle (`useTriageReturnFocus`).
 */
export function CloseTriageDialog({
  entry,
  entryGone = false,
  open,
  onOpenChange,
}: CloseTriageDialogProps) {
  const { t } = useTranslation();
  // What had the focus as it opened, read before the note box takes it.
  const onCloseAutoFocus = useTriageReturnFocus(open, entry);
  const kind = entry ? triageKind(entry) : "idea";
  return (
    <Dialog open={open && entry !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" onCloseAutoFocus={onCloseAutoFocus}>
        <DialogHeader>
          <DialogTitle>{t(`roadmap.triage.closeTitle.${kind}`)}</DialogTitle>
          <DialogDescription>{t(`roadmap.triage.closeDescription.${kind}`)}</DialogDescription>
        </DialogHeader>
        {/* Mounted while open only, so every opening starts without a note. */}
        {open && entry && (
          <CloseTriageForm
            key={entry.id}
            entry={entry}
            entryGone={entryGone}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface CloseTriageFormProps {
  entry: RoadmapTriageEntry;
  /** The entry has left the triage meanwhile: handled elsewhere. */
  entryGone: boolean;
  /** Back to the table: after the close went through, or on Annuleren. */
  onDone: () => void;
}

function CloseTriageForm({ entry, entryGone, onDone }: CloseTriageFormProps) {
  const { t } = useTranslation();
  const ids = useId();
  const close = useCloseRoadmapTriage();
  const [note, setNote] = useState("");
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const kind = triageKind(entry);
  // Left the triage, and not through this form's own close (which ends the dialog once the roadmap shows it).
  const handled = entryGone && (close.isIdle || close.isError);

  const submit = async () => {
    if (close.isPending || handled) {
      return;
    }
    setFailure(null);
    try {
      await close.mutateAsync({ entryId: entry.id, note });
      onDone();
    } catch (error) {
      setFailure(inlineFailure({ error }));
    }
  };

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <TriageEntryTitle entry={entry} />
      <Field
        label={t("roadmap.triage.closeNote")}
        htmlFor={`${ids}-note`}
        hint={t("roadmap.triage.closeNoteHint")}
      >
        <textarea
          id={`${ids}-note`}
          value={note}
          maxLength={CLOSE_NOTE_MAX}
          rows={3}
          onChange={(event) => setNote(event.target.value)}
          className={TEXTAREA_CLASS}
          autoFocus
        />
      </Field>
      {handled ? (
        <p role="alert" className="text-destructive text-sm">
          {t("errors.codes.roadmap_triage_closed")}
        </p>
      ) : (
        failure && (
          <p role="alert" className="text-destructive text-sm">
            {apiErrorMessage(failure.error, "closeRoadmapTriage")}
          </p>
        )
      )}
      <StepButtons
        onBack={onDone}
        confirm={t(`roadmap.triage.closeTitle.${kind}`)}
        pending={close.isPending}
        disabled={handled}
      />
    </form>
  );
}
